import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    AboutResponse,
    CustomerGroupRequest,
    CustomerGroupResponse,
    HealthResponse,
    InitPaymentDto,
    InitPaymentResponseDto,
    JobDetailResponse,
    JobTriggerRequest,
    MerchantDetailResponse,
    MerchantRequest,
    MerchantResponse,
    OrderPaymentResponse,
    PaymentDto,
    PaymentMethodRequest,
    PaymentMethodResponse,
    PaymentsPortalPropertyRequest,
    PaymentsPortalPropertyResponse,
    PosCertificateRequest,
    PosCertificateResponse,
    PosPaymentMethodCurrencyConflict,
    PosPaymentMethodPlacementRequestPair,
    PosPaymentMethodPlacementResponse,
    PosPaymentMethodRequest,
    PosPaymentMethodResponse,
    PosProviderPaymentMethodResponse,
    PosRequest,
    PosResponse
} from './api.models';

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private readonly BASE = 'admin';
    private readonly MERCHANT_ACCOUNT = `${this.BASE}/merchant-accounts`;

    constructor(private http: HttpClient) {
    }

    getAbout(): Observable<AboutResponse> {
        return this.http.get<AboutResponse>(`/admin/project-info`);
    }

    getHealth(): Observable<HealthResponse> {
        return this.http.get<HealthResponse>(`/status/health`);
    }

    getOrder(id: string): Observable<OrderPaymentResponse> {
        return this.http.get<OrderPaymentResponse>(`/orders/${id}`);
    }

    /* Payments api */

    initPayment(payload: InitPaymentDto) {
        return this.http.post<InitPaymentResponseDto>(`/payments`, payload);
    }

    getPayment(id: string) {
        return this.http.get<PaymentDto>(`/payments/${id}`);
    }

    /* Merchants api */

    getMerchants() {
        return this.http.get<MerchantResponse[]>(`/admin/merchant-accounts`);
    }

    getMerchant(id: string) {
        return this.http.get<MerchantDetailResponse>(`/admin/merchant-accounts/${id}`);
    }

    deleteMerchant(id: string) {
        return this.http.delete<void>(`/admin/merchant-accounts/${id}`);
    }

    postMerchant(merchant: MerchantRequest) {
        return this.http.post<MerchantDetailResponse>(`/admin/merchant-accounts`, merchant);
    }

    putMerchant(id: string, merchant: MerchantRequest) {
        return this.http.put<MerchantDetailResponse>(`/admin/merchant-accounts/${id}`, merchant);
    }

    /* Customer groups api */

    getCustomerGroups() {
        return this.http.get<CustomerGroupResponse[]>(`/admin/customer-groups`);
    }

    getCustomerGroup(id: string) {
        return this.http.get<CustomerGroupResponse>(`/admin/customer-groups/${id}`);
    }

    postCustomerGroup(customerGroup: CustomerGroupRequest) {
        return this.http.post<CustomerGroupResponse>(`/admin/customer-groups`, customerGroup);
    }

    putCustomerGroup(id: string, customerGroup: CustomerGroupRequest) {
        return this.http.put<CustomerGroupResponse>(`/admin/customer-groups/${id}`, customerGroup);
    }

    deleteCustomerGroup(id: string) {
        return this.http.delete<void>(`/admin/customer-groups/${id}`);
    }

    /* Payment methods api */

    getPaymenMethods() {
        return this.http.get<PaymentMethodResponse[]>(`/admin/payment-methods`);
    }

    getPaymentMethod(id: string) {
        return this.http.get<PaymentMethodResponse>(`/admin/payment-methods/${id}`);
    }

    postPaymentMethod(paymentMethod: PaymentMethodRequest) {
        return this.http.post<PaymentMethodResponse>(`/admin/payment-methods`, paymentMethod);
    }

    putPaymentMethod(id: string, paymentMethod: PaymentMethodRequest) {
        return this.http.put<PaymentMethodResponse>(`/admin/payment-methods/${id}`, paymentMethod);
    }

    deletePaymentMethod(id: string) {
        return this.http.delete<void>(`/admin/payment-methods/${id}`);
    }

    /* Pos api */

    getPoses() {
        return this.http.get<PosResponse[]>(`/admin/pos`);
    }

    getPos(id: string) {
        return this.http.get<PosResponse>(`/admin/pos/${id}`);
    }

    postPos(pos: PosRequest) {
        return this.http.post<PosResponse>(`/admin/pos`, pos);
    }

    putPos(id: string, pos: PosRequest) {
        return this.http.put<PosResponse>(`/admin/pos/${id}`, pos);
    }

    deletePos(id: string) {
        return this.http.delete<void>(`/admin/pos/${id}`);
    }

    /* POS payment method api */

    getCurrencyConflicts() {
        return this.http.get<PosPaymentMethodCurrencyConflict[]>(`/admin/pos-payment-methods/currency-conflicts`);
    }

    getPlacement(posId: string) {
        return this.http.get<PosPaymentMethodPlacementResponse>(`/admin/pos-payment-methods/placement?posId=${posId}`);
    }

    editPlacement(request: PosPaymentMethodPlacementRequestPair[]) {
        return this.http.put<void>(`/admin/pos-payment-methods/placement`, request);
    }

    getPosPaymentMethods() {
        return this.http.get<PosPaymentMethodResponse[]>(`/admin/pos-payment-methods`);
    }

    getPosPaymentMethod(id: string) {
        return this.http.get<PosPaymentMethodResponse>(`/admin/pos-payment-methods/${id}`);
    }

    postPosPaymentMethod(posPaymentMethod: PosPaymentMethodRequest) {
        return this.http.post<PosPaymentMethodResponse>(`/admin/pos-payment-methods`, posPaymentMethod);
    }

    putPosPaymentMethod(id: string, posPaymetdMethod: PosPaymentMethodRequest) {
        return this.http.put<PosPaymentMethodResponse>(`/admin/pos-payment-methods/${id}`, posPaymetdMethod);
    }

    deletePosPaymentMethod(id: string) {
        return this.http.delete<void>(`/admin/pos-payment-methods/${id}`);
    }

    /* POS certificate api */

    getPosCertificates(posId: string) {
        return this.http.get<PosCertificateResponse[]>(`/admin/pos/${posId}/certificates`);
    }

    getPosCertificate(id: string) {
        return this.http.get<PosCertificateResponse>(`/admin/pos/certificates/${id}`);
    }

    postPosCertificate(posId: string, posCertificate: PosCertificateRequest) {
        return this.http.post<PosCertificateResponse>(`/admin/pos/${posId}/certificates`, posCertificate);
    }

    putPosCertificate(id: string, posCertificate: PosCertificateRequest) {
        return this.http.put<PosCertificateResponse>(`/admin/pos/certificates/${id}`, posCertificate);
    }

    deletePosCertificate(id: string) {
        return this.http.delete<void>(`/admin/pos/certificates/${id}`);
    }

    /* Card api */

    deleteCard(id: string) {
        return this.http.delete<void>(`/cards/${id}`);
    }

    /* Pos rovider payment method API */

    getPosProviderPaymentMethods(posId: string) {
        return this.http.get<PosProviderPaymentMethodResponse[]>(`/admin/pos-provider-payment-methods/${posId}`);
    }

    postPosProviderPaymentMethod(posId: string, method: string) {
        return this.http.post(`/admin/pos-provider-payment-methods/${posId}`, method);
    }

    deletePosProviderPaymentMethod(posId: string, method: string) {
        return this.http.delete(`/admin/pos-provider-payment-methods/${posId}/${method}`);
    }

    /* Swish api */

    getSwishQRCodeBase64(token: string) {
        return this.http.get(`/swish/qr?token=${token}`, { responseType: 'text' });
    }

    /* Properties api */

    getProperties() {
        return this.http.get<PaymentsPortalPropertyResponse[]>(`/admin/properties`);
    }

    putProperty(key: string, body: PaymentsPortalPropertyRequest) {
        return this.http.put<PaymentsPortalPropertyResponse>(`/admin/properties/${key}`, body);
    }

    /* Jobs api */

    getJobs() {
        return this.http.get<JobDetailResponse[]>(`/admin/jobs`);
    }

    scheduleJob(body: JobTriggerRequest) {
        return this.http.post<JobDetailResponse>(`/admin/jobs/schedule`, body);
    }

    triggerJob(group: string, name: string) {
        return this.http.post<JobDetailResponse>(`/admin/jobs/trigger/${group}/${name}`, {});
    }

    pauseJob(group: string, name: string) {
        return this.http.put<JobDetailResponse>(`/admin/jobs/pause/${group}/${name}`, {});
    }

    resumeJob(group: string, name: string) {
        return this.http.put<JobDetailResponse>(`/admin/jobs/resume/${group}/${name}`, {});
    }
}
